import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, convertImageToBase64, deleteImage } from '../utils/imageUpload';
import { showSuccess, showError, showWarning } from '../utils/toast';

const QuestionLibraryManager = () => {
  const [libraryQuestions, setLibraryQuestions] = useState([]);
  const [exams, setExams] = useState([]);
  const [questionsUsageMap, setQuestionsUsageMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterSubject, setFilterSubject] = useState('all');
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    questionImage: '',
    questionType: 'mcq',
    marks: '',
    difficultyLevel: 'medium',
    subjectName: '',
    options: [
      { text: '', image: '', isCorrect: false },
      { text: '', image: '', isCorrect: false }
    ],
    correctAnswer: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchLibraryQuestions(), fetchExams()]);
  };

  const fetchLibraryQuestions = async () => {
    try {
      setLoading(true);
      const adminId = localStorage.getItem('adminId');
      const q = query(collection(db, 'questionLibrary'), where('organizationId', '==', adminId));
      const querySnapshot = await getDocs(q);
      const questionsList = [];
      querySnapshot.forEach((doc) => {
        questionsList.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by creation date (newest first)
      questionsList.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      setLibraryQuestions(questionsList);
      
      // Extract unique subjects
      extractAvailableSubjects(questionsList);
      
      // After fetching questions, map their usage
      await mapQuestionsToExams(questionsList);
    } catch (error) {
      console.error('Error fetching library questions:', error);
      showError('Failed to load question library');
    } finally {
      setLoading(false);
    }
  };

  const fetchExams = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(collection(db, 'exams'), where('organizationId', '==', adminId));
      const querySnapshot = await getDocs(q);
      const examsList = [];
      querySnapshot.forEach((doc) => {
        examsList.push({ id: doc.id, ...doc.data() });
      });
      setExams(examsList);
      return examsList;
    } catch (error) {
      console.error('Error fetching exams:', error);
      return [];
    }
  };

  const mapQuestionsToExams = async (questions) => {
    try {
      const adminId = localStorage.getItem('adminId');
      const examsQuery = query(collection(db, 'exams'), where('organizationId', '==', adminId));
      const examsSnapshot = await getDocs(examsQuery);
      
      const usageMap = {};
      
      // Initialize usage map for all questions
      questions.forEach(q => {
        usageMap[q.id] = [];
      });

      // Check each exam for question usage
      examsSnapshot.forEach((examDoc) => {
        const examData = examDoc.data();
        const examInfo = {
          id: examDoc.id,
          name: examData.name || 'Untitled Exam'
        };

        // Check if exam has questions
        if (examData.questions && Array.isArray(examData.questions)) {
          examData.questions.forEach(question => {
            if (question.libraryId && usageMap[question.libraryId] !== undefined) {
              usageMap[question.libraryId].push(examInfo);
            }
          });
        }
      });

      setQuestionsUsageMap(usageMap);
    } catch (error) {
      console.error('Error mapping questions to exams:', error);
    }
  };

  const resetForm = () => {
    setQuestionForm({
      questionText: '',
      questionImage: '',
      questionType: 'mcq',
      marks: '',
      difficultyLevel: 'medium',
      subjectName: '',
      options: [
        { text: '', image: '', isCorrect: false },
        { text: '', image: '', isCorrect: false }
      ],
      correctAnswer: ''
    });
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!questionForm.questionText && !questionForm.questionImage) {
        showError('Please provide either question text or image');
        setLoading(false);
        return;
      }

      if (!questionForm.marks || questionForm.marks <= 0) {
        showError('Please enter valid marks');
        setLoading(false);
        return;
      }

      if (questionForm.questionType === 'mcq') {
        const validOptions = questionForm.options.filter(opt => opt.text || opt.image);
        if (validOptions.length < 2) {
          showError('MCQ must have at least 2 options');
          setLoading(false);
          return;
        }

        const correctOptions = questionForm.options.filter(opt => opt.isCorrect);
        if (correctOptions.length === 0) {
          showError('Please mark at least one correct answer');
          setLoading(false);
          return;
        }
      }

      const adminId = localStorage.getItem('adminId');
      const questionData = {
        questionText: questionForm.questionText,
        questionImage: questionForm.questionImage,
        questionType: questionForm.questionType,
        marks: parseFloat(questionForm.marks),
        difficultyLevel: questionForm.difficultyLevel,
        subjectName: questionForm.subjectName || '',
        organizationId: adminId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (questionForm.questionType === 'mcq') {
        questionData.options = questionForm.options.filter(opt => opt.text || opt.image);
      } else {
        questionData.correctAnswer = questionForm.correctAnswer;
      }

      await addDoc(collection(db, 'questionLibrary'), questionData);
      
      showSuccess('Question added to library successfully!');
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error adding question:', error);
      showError('Failed to add question');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      questionText: question.questionText,
      questionImage: question.questionImage || '',
      questionType: question.questionType,
      marks: question.marks,
      difficultyLevel: question.difficultyLevel,
      subjectName: question.subjectName || '',
      options: question.options || [{ text: '', image: '', isCorrect: false }, { text: '', image: '', isCorrect: false }],
      correctAnswer: question.correctAnswer || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!questionForm.questionText && !questionForm.questionImage) {
        showError('Please provide either question text or image');
        setLoading(false);
        return;
      }

      if (!questionForm.marks || questionForm.marks <= 0) {
        showError('Please enter valid marks');
        setLoading(false);
        return;
      }

      if (questionForm.questionType === 'mcq') {
        const validOptions = questionForm.options.filter(opt => opt.text || opt.image);
        if (validOptions.length < 2) {
          showError('MCQ must have at least 2 options');
          setLoading(false);
          return;
        }

        const correctOptions = questionForm.options.filter(opt => opt.isCorrect);
        if (correctOptions.length === 0) {
          showError('Please mark at least one correct answer');
          setLoading(false);
          return;
        }
      }

      const questionData = {
        questionText: questionForm.questionText,
        questionImage: questionForm.questionImage,
        questionType: questionForm.questionType,
        marks: parseFloat(questionForm.marks),
        difficultyLevel: questionForm.difficultyLevel,
        subjectName: questionForm.subjectName || '',
        updatedAt: new Date().toISOString()
      };

      if (questionForm.questionType === 'mcq') {
        questionData.options = questionForm.options.filter(opt => opt.text || opt.image);
      } else {
        questionData.correctAnswer = questionForm.correctAnswer;
      }

      await updateDoc(doc(db, 'questionLibrary', editingQuestion.id), questionData);
      
      showSuccess('Question updated successfully!');
      setShowEditModal(false);
      setEditingQuestion(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error updating question:', error);
      showError('Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    // Check if question is used in any exams
    const usedInExams = questionsUsageMap[questionId] || [];
    
    if (usedInExams.length > 0) {
      const examNames = usedInExams.map(e => e.name).join(', ');
      if (!window.confirm(`This question is used in the following exam(s): ${examNames}.\n\nDeleting it will remove it from those exams. Are you sure?`)) {
        return;
      }
    } else {
      if (!window.confirm('Are you sure you want to delete this question from library?')) {
        return;
      }
    }

    try {
      const questionToDelete = libraryQuestions.find(q => q.id === questionId);
      
      // Delete images
      if (questionToDelete?.questionImage) {
        await deleteImage(questionToDelete.questionImage);
      }
      
      if (questionToDelete?.options && questionToDelete.options.length > 0) {
        for (const option of questionToDelete.options) {
          if (option.image) {
            await deleteImage(option.image);
          }
        }
      }
      
      await deleteDoc(doc(db, 'questionLibrary', questionId));
      showSuccess('Question deleted from library!');
      fetchData();
    } catch (error) {
      console.error('Error deleting question:', error);
      showError('Failed to delete question');
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setQuestionForm({
      ...questionForm,
      [name]: value
    });
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index][field] = value;
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const handleOptionCorrectToggle = (index) => {
    const newOptions = [...questionForm.options];
    newOptions[index].isCorrect = !newOptions[index].isCorrect;
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const addOption = () => {
    if (questionForm.options.length >= 6) {
      showWarning('Maximum 6 options allowed');
      return;
    }
    setQuestionForm({
      ...questionForm,
      options: [...questionForm.options, { text: '', image: '', isCorrect: false }]
    });
  };

  const removeOption = (index) => {
    if (questionForm.options.length <= 2) {
      showWarning('Minimum 2 options required');
      return;
    }
    const newOptions = questionForm.options.filter((_, i) => i !== index);
    setQuestionForm({
      ...questionForm,
      options: newOptions
    });
  };

  const handleImageUpload = async (e, field, optionIndex = null) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError('Please select an image file');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showError('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    
    // Try uploading to API first
    const uploadType = field === 'questionImage' ? 'questions' : 'options';
    const result = await uploadImage(file, 'oesweb', uploadType);
    
    if (result.success) {
      // API upload succeeded
      if (field === 'questionImage') {
        setQuestionForm({ ...questionForm, questionImage: result.url });
      } else if (field === 'optionImage' && optionIndex !== null) {
        handleOptionChange(optionIndex, 'image', result.url);
      }
      setUploadingImage(false);
      showSuccess('Image uploaded successfully!');
    } else {
      // API upload failed, fallback to base64
      try {
        const base64Image = await convertImageToBase64(file);
        if (field === 'questionImage') {
          setQuestionForm({ ...questionForm, questionImage: base64Image });
        } else if (field === 'optionImage' && optionIndex !== null) {
          handleOptionChange(optionIndex, 'image', base64Image);
        }
        setUploadingImage(false);
        showSuccess('Image loaded successfully!');
      } catch (base64Error) {
        setUploadingImage(false);
        showError('Image upload failed: ' + result.error);
      }
    }
  };

  const handleRemoveImage = async (field, optionIndex = null) => {
    try {
      let imageUrl = '';
      
      if (field === 'questionImage') {
        imageUrl = questionForm.questionImage;
        setQuestionForm({ ...questionForm, questionImage: '' });
      } else if (field === 'optionImage' && optionIndex !== null) {
        imageUrl = questionForm.options[optionIndex].image;
        handleOptionChange(optionIndex, 'image', '');
      }

      if (imageUrl) {
        await deleteImage(imageUrl);
      }
      
      showSuccess('Image removed successfully!');
    } catch (error) {
      console.error('Error removing image:', error);
      showError('Failed to remove image');
    }
  };

  const extractAvailableSubjects = (questionsList) => {
    // Extract unique non-empty subject names
    const subjects = [...new Set(
      questionsList
        .map(q => q.subjectName)
        .filter(subject => subject && subject.trim() !== '')
    )].sort();
    setAvailableSubjects(subjects);
  };

  const getFilteredQuestions = () => {
    return libraryQuestions.filter(question => {
      const matchesSearch = question.questionText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           question.subjectName?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || question.questionType === filterType;
      const matchesDifficulty = filterDifficulty === 'all' || question.difficultyLevel === filterDifficulty;
      const matchesSubject = filterSubject === 'all' || 
                            (filterSubject === 'none' && (!question.subjectName || question.subjectName.trim() === '')) ||
                            question.subjectName === filterSubject;
      return matchesSearch && matchesType && matchesDifficulty && matchesSubject;
    });
  };

  const toggleQuestionExpanded = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'mcq': return 'Multiple Choice';
      case 'true-false': return 'True/False';
      case 'short-answer': return 'Short Answer';
      default: return type;
    }
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Question Library</h2>
          <p className="text-gray-600 mt-1">Manage all questions for your organization</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition flex items-center gap-2 shadow-lg"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add New Question
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Questions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{libraryQuestions.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Used Questions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {Object.values(questionsUsageMap).filter(usage => usage.length > 0).length}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Unused Questions</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">
                {Object.values(questionsUsageMap).filter(usage => usage.length === 0).length}
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <svg className="h-8 w-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Exams</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{exams.length}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search questions..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject/Chapter</label>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Subjects</option>
              <option value="none">No Subject</option>
              {availableSubjects.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="mcq">Multiple Choice</option>
              <option value="true-false">True/False</option>
              <option value="short-answer">Short Answer</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:ring-transparent"
            >
              <option value="all">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredQuestions.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No questions found</h3>
            <p className="mt-1 text-gray-500">Get started by adding a new question to your library.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredQuestions.map((question, index) => {
              const usedInExams = questionsUsageMap[question.id] || [];
              const isExpanded = expandedQuestions[question.id];

              return (
                <div key={question.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-semibold text-gray-500">#{index + 1}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(question.difficultyLevel)}`}>
                          {question.difficultyLevel?.toUpperCase()}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {getTypeLabel(question.questionType)}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                          {question.marks} {question.marks === 1 ? 'Mark' : 'Marks'}
                        </span>
                        {question.subjectName && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                            📚 {question.subjectName}
                          </span>
                        )}
                      </div>

                      <div className="mt-2">
                        <p className="text-gray-900 font-medium">{question.questionText || 'Question with image only'}</p>
                        {question.questionImage && (
                          <img 
                            src={question.questionImage} 
                            alt="Question" 
                            className="mt-3 max-w-md rounded-lg border-2 border-gray-300 shadow-sm cursor-pointer hover:shadow-md transition"
                            onClick={() => window.open(question.questionImage, '_blank')}
                            title="Click to view full size"
                          />
                        )}
                      </div>

                      {/* Options Preview */}
                      {question.questionType === 'mcq' && question.options && (
                        <div className="mt-3">
                          <button
                            onClick={() => toggleQuestionExpanded(question.id)}
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            {isExpanded ? (
                              <>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                                Hide Options
                              </>
                            ) : (
                              <>
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                Show {question.options.length} Options
                              </>
                            )}
                          </button>

                          {isExpanded && (
                            <div className="mt-2 space-y-2">
                              {question.options.map((option, optIdx) => (
                                <div 
                                  key={optIdx} 
                                  className={`p-3 rounded-lg border-2 ${option.isCorrect ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {option.isCorrect && (
                                      <svg className="h-5 w-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    )}
                                    <span className="font-semibold text-gray-700">{String.fromCharCode(65 + optIdx)}.</span>
                                    <span className="text-gray-900">{option.text}</span>
                                  </div>
                                  {option.image && (
                                    <img 
                                      src={option.image} 
                                      alt={`Option ${optIdx + 1}`} 
                                      className="mt-3 max-w-sm rounded-lg border-2 border-gray-300 shadow-sm cursor-pointer hover:shadow-md transition"
                                      onClick={() => window.open(option.image, '_blank')}
                                      title="Click to view full size"
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Usage Information */}
                      <div className="mt-4">
                        {usedInExams.length === 0 ? (
                          <div className="flex items-center gap-2 text-orange-600">
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium">Not used in any exam</span>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Used in {usedInExams.length} exam(s):</p>
                            <div className="flex flex-wrap gap-2">
                              {usedInExams.map((exam) => (
                                <span 
                                  key={exam.id} 
                                  className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium"
                                >
                                  {exam.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Timestamps */}
                      <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                        {question.createdAt && (
                          <span>Created: {new Date(question.createdAt).toLocaleDateString()}</span>
                        )}
                        {question.updatedAt && question.updatedAt !== question.createdAt && (
                          <span>Updated: {new Date(question.updatedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEditQuestion(question)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="Edit Question"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete Question"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Question Modal */}
      {showAddModal && (
        <QuestionModal
          title="Add New Question"
          questionForm={questionForm}
          handleFormChange={handleFormChange}
          handleOptionChange={handleOptionChange}
          handleOptionCorrectToggle={handleOptionCorrectToggle}
          addOption={addOption}
          removeOption={removeOption}
          handleImageUpload={handleImageUpload}
          handleRemoveImage={handleRemoveImage}
          handleSubmit={handleAddQuestion}
          onClose={() => {
            setShowAddModal(false);
            resetForm();
          }}
          loading={loading}
          uploadingImage={uploadingImage}
        />
      )}

      {/* Edit Question Modal */}
      {showEditModal && (
        <QuestionModal
          title="Edit Question"
          questionForm={questionForm}
          handleFormChange={handleFormChange}
          handleOptionChange={handleOptionChange}
          handleOptionCorrectToggle={handleOptionCorrectToggle}
          addOption={addOption}
          removeOption={removeOption}
          handleImageUpload={handleImageUpload}
          handleRemoveImage={handleRemoveImage}
          handleSubmit={handleUpdateQuestion}
          onClose={() => {
            setShowEditModal(false);
            setEditingQuestion(null);
            resetForm();
          }}
          loading={loading}
          uploadingImage={uploadingImage}
        />
      )}
    </div>
  );
};

// Question Modal Component
const QuestionModal = ({
  title,
  questionForm,
  handleFormChange,
  handleOptionChange,
  handleOptionCorrectToggle,
  addOption,
  removeOption,
  handleImageUpload,
  handleRemoveImage,
  handleSubmit,
  onClose,
  loading,
  uploadingImage
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Question Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Question Type *</label>
            <select
              name="questionType"
              value={questionForm.questionType}
              onChange={handleFormChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="mcq">Multiple Choice (MCQ)</option>
              <option value="true-false">True/False</option>
              <option value="short-answer">Short Answer</option>
            </select>
          </div>

          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Question Text</label>
            <textarea
              name="questionText"
              value={questionForm.questionText}
              onChange={handleFormChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter question text..."
            ></textarea>
          </div>

          {/* Question Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Question Image (Optional)</label>
            {questionForm.questionImage ? (
              <div className="relative inline-block">
                <img 
                  src={questionForm.questionImage} 
                  alt="Question" 
                  className="w-full max-w-md h-auto rounded-lg border-2 border-gray-300 shadow-sm"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23666">Image Load Error</text></svg>';
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage('questionImage')}
                  className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 shadow-lg"
                  title="Remove Image"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'questionImage')}
                  className="hidden"
                  id="question-image-upload"
                  disabled={uploadingImage}
                />
                <label
                  htmlFor="question-image-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {uploadingImage ? 'Uploading...' : 'Upload Image'}
                </label>
              </div>
            )}
          </div>

          {/* Subject/Concept Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject / Concept Name (Optional)</label>
            <input
              type="text"
              name="subjectName"
              value={questionForm.subjectName}
              onChange={handleFormChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Physics - Mechanics, Math - Algebra, etc."
            />
            <p className="text-xs text-gray-500 mt-1">Helps categorize questions by subject or topic</p>
          </div>

          {/* Marks and Difficulty */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Marks *</label>
              <input
                type="number"
                name="marks"
                value={questionForm.marks}
                onChange={handleFormChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                min="0.25"
                step="0.25"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty Level *</label>
              <select
                name="difficultyLevel"
                value={questionForm.difficultyLevel}
                onChange={handleFormChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          {/* MCQ Options */}
          {questionForm.questionType === 'mcq' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">Options *</label>
                <button
                  type="button"
                  onClick={addOption}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Option
                </button>
              </div>

              {questionForm.options.map((option, index) => (
                <div key={index} className="border border-gray-300 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-gray-700">Option {String.fromCharCode(65 + index)}</span>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={option.isCorrect}
                          onChange={() => handleOptionCorrectToggle(index)}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Correct Answer</span>
                      </label>
                      {questionForm.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                    placeholder="Option text..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />

                  {option.image ? (
                    <div className="relative inline-block mt-2">
                      <img 
                        src={option.image} 
                        alt={`Option ${index + 1}`} 
                        className="w-full max-w-sm h-auto rounded-lg border-2 border-gray-300 shadow-sm"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23666">Image Load Error</text></svg>';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage('optionImage', index)}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 shadow-lg"
                        title="Remove Image"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'optionImage', index)}
                        className="hidden"
                        id={`option-image-${index}`}
                        disabled={uploadingImage}
                      />
                      <label
                        htmlFor={`option-image-${index}`}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-sm transition"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {uploadingImage ? 'Uploading...' : 'Add Image'}
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Short Answer */}
          {questionForm.questionType === 'short-answer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer *</label>
              <input
                type="text"
                name="correctAnswer"
                value={questionForm.correctAnswer}
                onChange={handleFormChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                placeholder="Enter correct answer..."
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400"
              disabled={loading || uploadingImage}
            >
              {loading ? 'Saving...' : 'Save Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionLibraryManager;
